import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Truck, RefreshCw } from 'lucide-react';
import { Layout } from '../components/Layout';
import { submitContact } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    content: '+49 17667405770',
    link: 'tel:+4917667405770',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'prabalbansal11@gmail.com',
    link: 'mailto:prabalbansal11@gmail.com',
  },
  {
    icon: Clock,
    title: 'Hours',
    content: 'Mon-Fri: 9am - 6pm CET',
    link: null,
  },
];

export const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      await submitContact(formData);
      toast.success('Message sent successfully!', {
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#F2EFE9] py-16 md:py-24" data-testid="contact-header">
        <div className="container-luxury text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-5xl text-[#2C2420] mb-4"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-base text-[#5D5550] max-w-xl mx-auto"
          >
            We'd love to hear from you. Whether you have a question about our products, 
            shipping, or anything else—our team is here to help.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing" data-testid="contact-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-xs uppercase tracking-widest text-[#8C8580] mb-6">
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm text-[#5D5550]">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-underline"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm text-[#5D5550]">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-underline"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <Label htmlFor="subject" className="text-sm text-[#5D5550]">
                    Subject
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="input-underline"
                    placeholder="e.g., Order inquiry, Product question"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm text-[#5D5550]">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="input-underline resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-4 h-4" />
                      Sending...
                    </span>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-xs uppercase tracking-widest text-[#8C8580] mb-6">
                Contact Information
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info) => (
                  <div key={info.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#F2EFE9] flex items-center justify-center flex-shrink-0">
                      <info.icon className="h-5 w-5 text-[#C6A87C]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#8C8580] mb-1">
                        {info.title}
                      </p>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-[#2C2420] hover:text-[#C6A87C] transition-colors"
                          data-testid={`contact-info-${info.title.toLowerCase()}`}
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-[#2C2420]">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Policies */}
              <div className="space-y-8" id="shipping">
                <div className="bg-[#F2EFE9] p-6" data-testid="shipping-policy">
                  <div className="flex items-center gap-3 mb-4">
                    <Truck className="h-5 w-5 text-[#C6A87C]" />
                    <h3 className="font-heading text-lg text-[#2C2420]">
                      Shipping Policy
                    </h3>
                  </div>
                  <ul className="space-y-2 text-sm text-[#5D5550]">
                    <li>• Free worldwide shipping on orders above €150</li>
                    <li>• Standard shipping: €15 (5-10 business days)</li>
                    <li>• Express shipping available at checkout</li>
                    <li>• All orders include tracking information</li>
                  </ul>
                </div>

                <div className="bg-[#F2EFE9] p-6" id="returns" data-testid="returns-policy">
                  <div className="flex items-center gap-3 mb-4">
                    <RefreshCw className="h-5 w-5 text-[#C6A87C]" />
                    <h3 className="font-heading text-lg text-[#2C2420]">
                      Returns & Refunds
                    </h3>
                  </div>
                  <ul className="space-y-2 text-sm text-[#5D5550]">
                    <li>• Free returns within 30 days of delivery</li>
                    <li>• Items must be unworn with original tags</li>
                    <li>• Full refund to original payment method</li>
                    <li>• Contact us to initiate a return</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
